import { useCallback, useEffect, useState } from "react";

import { api } from "@/redux/api";
import { useAppDispatch } from "@/redux/hooks";

/** How often the Feed asks again while a bulk add is still being processed. */
export const FEED_BULK_REFRESH_INTERVAL_MS = 30_000;

/** The bulk add's own copy promises "up to 5 minutes", so poll for at least that long. */
export const FEED_BULK_REFRESH_WINDOW_MS = 5 * 60_000;

/**
 * Refreshes the Feed's cards after the Add Person bulk add (PREC-5041 review S3).
 * The bulk endpoint only queues processing and returns before any `list_person`
 * exists, so one invalidation at success would read a list that has not changed.
 * `start()` invalidates immediately and then keeps the Feed fresh until the
 * promised processing window has passed; the timer stops when the card unmounts.
 */
export const useFeedBulkRefresh = (): (() => void) => {
	const dispatch = useAppDispatch();
	const [refreshUntil, setRefreshUntil] = useState<number | null>(null);

	const start = useCallback(() => {
		dispatch(api.util.invalidateTags(["Feed", "FeedCounts"]));
		setRefreshUntil(Date.now() + FEED_BULK_REFRESH_WINDOW_MS);
	}, [dispatch]);

	useEffect(() => {
		if (refreshUntil === null) {
			return;
		}

		const timer = window.setInterval(() => {
			if (Date.now() >= refreshUntil) {
				window.clearInterval(timer);

				return;
			}

			dispatch(api.util.invalidateTags(["Feed", "FeedCounts"]));
		}, FEED_BULK_REFRESH_INTERVAL_MS);

		return () => window.clearInterval(timer);
	}, [dispatch, refreshUntil]);

	return start;
};
